// Local/offline Q&A fallback (no external API required)

function normalize(input) {
  return (input || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

function hasAny(text, words) {
  return words.some((w) => text.includes(w));
}

function pickOne(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

const JOKES = [
  "Why don’t programmers like nature? It has too many bugs.",
  "I told my computer I needed a break… it said: ‘No problem, I’ll go to sleep.’",
  "Why did the developer go broke? Because they used up all their cache.",
  "What do you call 8 hobbits? A hobbyte.",
  "Why do Java developers wear glasses? Because they don’t C#.",
];

// A lightweight “session” model: the bot can suggest next steps.
// The UI can ignore these fields; they’re here if you want to extend later.
export const LOCAL_QNA = [
  {
    id: "greeting",
    patterns: [
      /^hi\b/,
      /^hello\b/,
      /^hey\b/,
      /^hii\b/,
      /^hy\b/,
      /^assalam o alaikum\b/,
      /^assalamualaikum\b/,
      /^asalam o alaikum\b/,
      /^salam\b/,
      /^aoa\b/,
      /^السلام علیکم\b/u,
    ],
    responses: [
      "Hi! I’m Vertex AI Tec support (offline mode). How can I help you today?",
      "Hello! How can I assist you—services, pricing, or support?",
      "Wa alaikum assalam! How can I help you today?",
    ],
    followUps: ["services", "pricing", "support"],
  },
  {
    id: "how_are_you",
    patterns: [
      /how are you/,
      /how r you/,
      /how are u/,
      /how r u/,
      /kese ho/,
      /kaise ho/,
      /kesi ho/,
      /kya haal/,
      /how's it going/,
    ],
    responses: [
      "I’m doing well—thanks for asking! What can I help you with today?",
      "All good here. Tell me what you need: services, pricing, or support.",
    ],
  },
  {
    id: "weather",
    patterns: [/weather/, /temperature/, /is it raining/, /forecast/],
    responses: [
      "I can’t access live weather in offline mode, but if you tell me your city I can suggest what to check and how to interpret the forecast.",
      "Offline mode can’t fetch real-time weather. Share your location and I’ll guide you on what to look for (rain chance, feels-like, wind).",
    ],
  },
  {
    id: "joke",
    patterns: [/tell.*joke/, /\bjoke\b/, /make me laugh/, /funny/],
    responses: JOKES,
  },
  {
    id: "thanks",
    patterns: [/thank you/, /\bthanks\b/, /shukriya/, /jazakallah/, /جزاک اللہ/u],
    responses: [
      "You’re welcome! Anything else you’d like to ask?",
      "Happy to help. Want services info, pricing, or support?",
    ],
  },
  {
    id: "goodbye",
    patterns: [/bye\b/, /goodbye\b/, /see you/, /allah hafiz/, /khuda hafiz/],
    responses: [
      "Bye! If you need anything later, just message here.",
      "Allah Hafiz! I’m here whenever you need support.",
    ],
  },
  {
    id: "pricing",
    patterns: [/pricing/, /price/, /charges/, /cost/, /rate/, /budget/, /quotation/, /quote/],
    responses: [
      "For pricing, tell me: the service you need (chatbot, website, automation), your timeline, and whether you want hosting + maintenance.",
      "Pricing depends on scope. Share requirements (features + deadline + preferred tech) and I’ll outline a rough estimate range.",
    ],
    followUps: ["chatbot", "website", "automation", "maintenance"],
  },
  {
    id: "services",
    patterns: [/services/, /what do you do/, /what can you do/, /offer/, /solutions/],
    responses: [
      "We can help with AI chatbots, WhatsApp automation, web apps, APIs, and customer-support workflows. What are you building?",
      "Our common work: AI assistants, WhatsApp bots, integrations (CRM/Sheets), web dashboards, and support ticket flows. What’s your goal?",
    ],
  },
  {
    id: "whatsapp_bot",
    patterns: [/whatsapp/, /wa bot/, /whats app/, /whatssms/],
    responses: [
      "WhatsApp bot basics: a provider webhook sends messages to your app; your app processes the text and sends a reply. You’ll need WhatsApp provider credentials. Want setup steps?",
      "For WhatsApp automation, we usually define intents, connect webhook, store conversations (MongoDB), and add escalation rules. What’s your use case?",
    ],
  },
  {
    id: "support",
    patterns: [/support/, /help/, /issue/, /problem/, /bug/, /not working/, /error/],
    responses: [
      "Tell me what’s not working and paste the exact error message. Also share what you were trying to do right before it happened.",
      "I can help troubleshoot. What component is failing—frontend (React), backend (FastAPI), database (MongoDB), or deployment (Docker)?",
    ],
    followUps: ["frontend", "backend", "mongodb", "docker"],
  },
  {
    id: "frontend",
    patterns: [/frontend/, /react/, /ui/, /page/, /npm/, /vite/, /react-scripts/],
    responses: [
      "Frontend tips: run `npm start`, check the browser console, and confirm the proxy is set to your backend URL. What error do you see?",
      "If the UI won’t load: verify Node version, delete `node_modules` + reinstall, and ensure the dev server port isn’t taken. What’s your current symptom?",
    ],
  },
  {
    id: "backend",
    patterns: [/backend/, /fastapi/, /uvicorn/, /api/, /endpoint/, /500/],
    responses: [
      "Backend troubleshooting: look at the traceback in the server logs and confirm your environment variables, database connection, and CORS settings. What error message do you see?",
      "If a route isn’t working: tell me the endpoint path and the exact response/error you get (status code + message).",
    ],
  },
  {
    id: "mongodb",
    patterns: [/mongo/, /mongodb/, /pymongo/, /connection refused/],
    responses: [
      "MongoDB troubleshooting: confirm MongoDB is running and your connection string points to the correct host/database. Are you using Docker or local Mongo?",
      "If DB connection fails, share your connection string (hide secrets) and the exact error message (timeout/refused/auth).",
    ],
  },
  {
    id: "docker",
    patterns: [/docker/, /docker compose/, /compose/, /container/, /image/, /port mapping/],
    responses: [
      "Docker approach: run backend + MongoDB with compose, then run the React dev server separately. Are you on Windows Docker Desktop?",
      "If containers won’t start, share `docker compose logs` output and which service is unhealthy (backend/mongodb).",
    ],
  },
  {
    id: "lead_session",
    patterns: [/i want to start/, /i want to build/, /project/, /need a chatbot/, /need a website/],
    responses: [
      "Great—quick questions so I can guide you: What’s the goal, who are the users, and what’s your deadline?",
      "Nice. What features do you need (login, payments, admin panel, WhatsApp integration), and what’s your expected launch date?",
    ],
  },
];

function detectIntent(text) {
  // Rough intent detection for offline analytics + better followups
  if (hasAny(text, ["dashboard", "panel", "analytics", "stats", "profile"])) return "dashboard";
  if (hasAny(text, ["hi", "hello", "hey", "salam", "aoa"])) return "greeting";
  if (hasAny(text, ["price", "pricing", "cost", "budget", "quote"])) return "pricing";
  if (hasAny(text, ["service", "services", "offer", "solutions"])) return "services";
  if (hasAny(text, ["whatsapp", "whatssms", "wa"])) return "whatsapp_bot";
  if (hasAny(text, ["error", "issue", "problem", "bug", "not working"])) return "support";
  if (hasAny(text, ["joke", "funny", "laugh"])) return "joke";
  if (hasAny(text, ["weather", "temperature", "forecast", "rain"])) return "weather";
  if (hasAny(text, ["thank", "thanks", "shukriya", "jazak"])) return "thanks";
  if (hasAny(text, ["bye", "goodbye", "allah hafiz", "khuda hafiz"])) return "goodbye";
  return "unknown";
}

function makeGenericHelpfulReply(text, profile) {
  const name = profile?.name ? ` ${profile.name}` : "";
  const starters = [
    `I can help with that${name}, but I’m in offline mode (no AI API / no live internet).`,
    `I can try to help${name}. Quick note: offline mode means no live browsing and no AI API.`,
  ];

  const questionType =
    text.startsWith("how ") ? "how" :
    text.startsWith("what ") ? "what" :
    text.startsWith("why ") ? "why" :
    text.startsWith("when ") ? "when" :
    text.startsWith("where ") ? "where" :
    text.startsWith("who ") ? "who" :
    text.endsWith("?") ? "question" :
    "statement";

  const promptsByType = {
    how: "Tell me what you’re trying to achieve and what you’ve tried so far.",
    what: "Tell me the context (app/screen) and what outcome you want.",
    why: "Share the goal and constraints, and I’ll suggest likely reasons and fixes.",
    when: "Give me the timeline and dependencies, and I’ll propose a plan.",
    where: "Tell me which part (frontend/backend/db) and I’ll point you to the right place.",
    who: "Tell me the audience (users/admin/team) and I’ll tailor the answer.",
    question: "Add 1–2 details (platform, goal, constraints) and I’ll help.",
    statement: "Can you rephrase as a question or share what you want to do next?",
  };

  const quickOptions =
    "If you want, pick one: **services**, **pricing**, **WhatsApp bot setup**, **troubleshooting**, **dashboard**.";

  return `${pickOne(starters)} ${promptsByType[questionType]} ${quickOptions}`;
}

export function getLocalResponse(userText, context = {}) {
  const text = normalize(userText);
  const profile = context.profile || {};
  if (!text) {
    return { text: "Say something and I’ll reply in offline mode.", intent: "unknown" };
  }

  // Direct exact-ish matches first
  for (const item of LOCAL_QNA) {
    if (item.patterns.some((p) => p.test(text))) {
      return { text: pickOne(item.responses), intent: item.id };
    }
  }

  // Intent-based fallback
  const intent = detectIntent(text);
  const byId = (id) => LOCAL_QNA.find((x) => x.id === id)?.responses || [];

  if (intent !== "unknown") {
    if (intent === "joke") return { text: pickOne(JOKES), intent };
    if (intent === "dashboard") {
      const name = profile?.name ? `, ${profile.name}` : "";
      return {
        intent,
        text:
          `Sure${name}. I can create a personalized dashboard (offline). ` +
          "It can show your name/company, chat stats, common topics, and quick actions. " +
          "Open the **Dashboard** tab to set your name and view stats.",
      };
    }
    const canned = byId(intent);
    if (canned.length) return { text: pickOne(canned), intent };
  }

  return { text: makeGenericHelpfulReply(text, profile), intent: "unknown" };
}

// Backwards compatible helper (returns only text)
export function getLocalAnswer(userText, context = {}) {
  return getLocalResponse(userText, context).text;
}

